/**
 *  ActivationCheckExterior script file
**/

import authService, { USER_ROLES } from '@/api/authService'
import campaignsService from '@/api/campaignsService'
import { ADVERT_TYPE_LABELS } from '@/utils/advertOptions'
import { CAMPAIGN_STATUSES, CHANNEL_OPTIONS_EXTERIOR } from '@/utils/campaignOptions'

export default {
  name: 'ActivationCheckExterior',
  mixins: [],
  components: {},
  props: {
    campaign: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      authData: authService.authData,
      provisioning: [],
      blockLoading: {},
      blockError: {},
      blockCleared: {},
      loading: false,
      statusLoading: false
    }
  },
  computed: {
    advertTypeLabels () {
      return ADVERT_TYPE_LABELS
    },
    showWaitScreen () {
      return !this.isExteriorAgency && this.isProvisioning
    },
    isProvisioning () {
      return this.campaign.status === CAMPAIGN_STATUSES.AGENCY_PROVISIONING
    },
    isExteriorAgency () {
      return this.authData.user.role === USER_ROLES.AGENCY_EXTERIOR
    },
    isSignaling () {
      return this.campaign.channels[0].type === CHANNEL_OPTIONS_EXTERIOR.SIGNALING
    },
    allImagesUploaded () {
      return this.creativityBlocks.every(b => b.hasCreativity)
    },
    creativityBlocks () {
      const blocks = {}
      for (const item of this.provisioning) {
        const key = `ID${item.product.id}-${item.advert.advert_type}_${item.advert.size}`
        if (!blocks[key]) {
          blocks[key] = {
            key,
            ids: [],
            product: item.product,
            productMetadata: item.product_metadata,
            hasCreativity: this.blockCleared[key] ? false : !!item.creativity_public_url,
            creativity_url: item.creativity_public_url,
            size: item.advert.size,
            type: item.advert.advert_type,
            loading: this.blockLoading[key] || false,
            aspectRatioError: this.blockError[key] || false
          }
        }
        blocks[key].ids.push(item.id)
      }
      return Object.values(blocks)
    }
  },
  async mounted () {
    if (!this.showWaitScreen) {
      this.loading = true
      await this.fetchProvisioning()
      this.loading = false
    }
  },
  methods: {
    back () {
      this.$emit('back')
    },
    async fetchProvisioning () {
      try {
        const data = await campaignsService.getExteriorProvisioning({ id: this.campaign.id })
        this.provisioning = data
      } catch (err) {
        // eslint-disable-next-line
        console.error('[activation-check-exterior.js] Error fetching provisioning', err)
      }
    },
    loadImage (file) {
      return new Promise(resolve => {
        const tmpUrl = URL.createObjectURL(file)
        const tmpImage = new Image()
        tmpImage.onload = () => resolve(tmpImage)
        tmpImage.src = tmpUrl
      })
    },
    async onFilePreview (block, file) {
      this.setLoading(block, true)
      const image = await this.loadImage(file)
      const [targetWidth, targetHeight] = block.size.toUpperCase().split('X').map(n => n.replace(/,/g, '.'))
      const aspectRatioError = image.width / image.height !== targetWidth / targetHeight
      this.setError(block, aspectRatioError)
      this.setLoading(block, false)
    },
    setLoading (block, flag) {
      this.$set(this.blockLoading, block.key, flag)
    },
    setError (block, flag) {
      this.$set(this.blockError, block.key, flag)
    },
    setCleared (block, flag) {
      this.$set(this.blockCleared, block.key, flag)
    },
    async onFileUpload (block, file) {
      this.setLoading(block, true)
      await campaignsService.saveExteriorProvisioningDetails(this.campaign.id, block.ids, file)
      await this.fetchProvisioning()
      this.setCleared(block, false)
      this.setLoading(block, false)
    },
    removeImage (block) {
      this.setCleared(block, true)
    },
    async updateCampaign () {
      this.statusLoading = true
      try {
        await campaignsService.updateStatus({
          id: this.campaign.id,
          status: CAMPAIGN_STATUSES.CENTRAL_READY
        })
        this.$emit('close')
      } catch (err) {
        // eslint-disable-next-line
        console.error('[activation-check-exterior.js] Error updating campaign status', err)
      }
      this.statusLoading = false
    }
  }
}
